import {
  makeStyles,
  tokens,
  Text,
  Body1,
  Caption1,
  ProgressBar,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  CheckmarkCircleFilled,
  CircleRegular,
} from '@fluentui/react-icons';
import type { AnalysisStep } from '../../types/discovery.types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalMNudge,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalMNudge,
  },
  stepIcon: {
    flexShrink: 0,
    width: '20px',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalXXS,
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXS,
  },
  activeStep: {
    fontWeight: 600,
  },
  completedStep: {
    color: tokens.colorNeutralForeground3,
  },
});

interface AnalysisProgressProps {
  currentStep: AnalysisStep | null;
  stepsCompleted: AnalysisStep[];
  progressPct: number;
  message: string;
  error: string | null;
}

const STEP_LABELS: Record<AnalysisStep, string> = {
  authenticate: 'Getting started',
  scan_sites: 'Scanning SharePoint sites',
  scan_files: 'Analysing file distribution',
  scan_content_types: 'Checking content organisation',
  calculate_score: 'Calculating readiness assessment',
  generate_ai_content: 'Analysing your responses',
  generate_report: 'Building your report',
  upload_report: 'Preparing your download',
};

// Form-only release: only the steps that actually run. The environment-scan
// steps are completed instantly server-side and are intentionally NOT shown —
// we never access the customer's environment in this mode, so surfacing
// "Scanning SharePoint sites" would be misleading and alarming.
const ALL_STEPS: AnalysisStep[] = [
  'authenticate',
  'generate_ai_content',
  'generate_report',
  'upload_report',
];

export function AnalysisProgressView({
  currentStep,
  stepsCompleted,
  progressPct,
  message,
  error,
}: AnalysisProgressProps) {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size={500} weight="semibold">
          Analysing Your Responses
        </Text>
        {/* progressPct < 0 => indeterminate (synchronous generation reports no
            mid-flight steps, so the bar animates until the report returns). */}
        <ProgressBar
          value={progressPct < 0 ? undefined : progressPct / 100}
          thickness="large"
        />
      </div>

      <div className={styles.stepList}>
        {ALL_STEPS.map((step) => {
          const isCompleted = stepsCompleted.includes(step);
          const isCurrent = step === currentStep;

          return (
            <div key={step} className={styles.step}>
              <span className={styles.stepIcon}>
                {isCompleted ? (
                  <CheckmarkCircleFilled
                    fontSize={18}
                    color={tokens.colorPaletteGreenForeground1}
                  />
                ) : isCurrent ? (
                  <Spinner size="tiny" />
                ) : (
                  <CircleRegular
                    fontSize={18}
                    color={tokens.colorNeutralForeground4}
                  />
                )}
              </span>
              <div className={styles.stepContent}>
                <Body1
                  className={
                    isCurrent
                      ? styles.activeStep
                      : isCompleted
                        ? styles.completedStep
                        : undefined
                  }
                >
                  {STEP_LABELS[step]}
                </Body1>
                {isCurrent && message && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {message}
                  </Caption1>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
